import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  AppConfigActions,
  AppConfigService,
  selectNaceBlockersConfig,
  selectRisksConfig,
  selectCountriesConfig,
  selectNaceBlockersConfigLoading,
  selectRisksConfigLoading,
  selectIsCountriesConfigLoading
} from "@redux";
import { CONFIGS } from "@config/Applications";
import { Notification } from "@components";
import { translationGroups, writeExcelFile } from "@utilities";

const AVAILABLE_ACTIONS = {
  [CONFIGS.NACE_BLOCKERS]: AppConfigActions.getNaceBlockers,
  [CONFIGS.RISKS]: AppConfigActions.getRiskList,
  [CONFIGS.COUNTRIES]: AppConfigActions.getCountryRiskList
};

const AVAILABLE_CONFIG_SELECTORS = {
  [CONFIGS.NACE_BLOCKERS]: selectNaceBlockersConfig,
  [CONFIGS.RISKS]: selectRisksConfig,
  [CONFIGS.COUNTRIES]: selectCountriesConfig
};

const AVAILABLE_LOADER_SELECTORS = {
  [CONFIGS.NACE_BLOCKERS]: selectNaceBlockersConfigLoading,
  [CONFIGS.RISKS]: selectRisksConfigLoading,
  [CONFIGS.COUNTRIES]: selectIsCountriesConfigLoading
};

const EXCEL_FILE_NAME = {
  [CONFIGS.NACE_BLOCKERS]: "Nace blockers settings",
  [CONFIGS.RISKS]: "Risk settings",
  [CONFIGS.COUNTRIES]: "Country settings"
};

const EXCEL_COL_WIDTH = {
  [CONFIGS.NACE_BLOCKERS]: [{ wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 50 }, { wch: 35 }],
  [CONFIGS.RISKS]: [{ wch: 30 }, { wch: 35 }, { wch: 35 }, { wch: 50 }],
  [CONFIGS.COUNTRIES]: [{ wch: 30 }, { wch: 35 }]
};

const EXCEL_SERVICES = {
  [CONFIGS.NACE_BLOCKERS]: AppConfigService.getNaceBlockers,
  [CONFIGS.RISKS]: AppConfigService.getRiskList,
  [CONFIGS.COUNTRIES]: AppConfigService.getCountryRiskList
};

const flattenCountryConfigData = (list = []) => {
  const listCopy = [...list];
  const flatList = [];

  for (const countryConfig of listCopy) {
    const countryItem = { ...countryConfig };

    for (const flag of countryItem.countryFlagsLists) {
      countryItem[flag.riskTitle] = flag.isActive;
    }

    delete countryItem.countryFlagsLists;
    flatList.push(countryItem);
  }
  return flatList;
};

const useAppConfigControl = ({ initialFilters, configKey }) => {
  const getAction = AVAILABLE_ACTIONS[configKey];
  const dispatch = useDispatch();
  const config = useSelector(AVAILABLE_CONFIG_SELECTORS[configKey]);
  const isLoading = useSelector(AVAILABLE_LOADER_SELECTORS[configKey]);

  const [tableFilters, setTableFilters] = useState(initialFilters);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  useEffect(() => {
    if (!config.isFiltered && config.items) return;
    (async () => {
      await dispatch(getAction(initialFilters));
    })();
  }, [dispatch]);

  const onFilter = useCallback(async filters => {
    const newFilterValues = {
      ...filters,
      page: initialFilters.page,
      rowsPerPage: initialFilters.rowsPerPage
    };
    setTableFilters(newFilterValues);
    await dispatch(getAction(newFilterValues, true));
  }, []);

  const downloadExcel = useCallback(async () => {
    setIsDownloadingExcel(true);
    try {
      const { items } = await EXCEL_SERVICES[configKey]({
        ...tableFilters,
        rowsPerPage: config.paging.totalItems || 1000
      });

      if (configKey === CONFIGS.COUNTRIES) {
        const flattened = flattenCountryConfigData(items);
        writeExcelFile({
          items: flattened,
          fileName: EXCEL_FILE_NAME[configKey],
          colWidths: EXCEL_COL_WIDTH[configKey]
        });
      } else {
        writeExcelFile({
          items,
          fileName: EXCEL_FILE_NAME[configKey],
          colWidths: EXCEL_COL_WIDTH[configKey]
        });
      }
    } catch (e) {
      Notification({
        type: "error",
        message: translationGroups.ErrorCode + "Unknown error",
        description: e.message,
        noTranslateDescription: true
      });
    } finally {
      setIsDownloadingExcel(false);
    }
  }, [config.paging.totalItems]);

  const onPageChange = useCallback(
    async (pageNumber, pageSize) => {
      const newRiskFilters = {
        ...tableFilters,
        page: pageNumber,
        rowsPerPage: pageSize
      };
      setTableFilters(newRiskFilters);
      await dispatch(getAction(newRiskFilters, true));
    },
    [tableFilters]
  );

  return {
    onFilter,
    onPageChange,
    tableFilters,
    config,
    isLoading,
    downloadExcel,
    isDownloadingExcel
  };
};

export default useAppConfigControl;
